import {
  LoadingAnimation,
  PlainButton,
} from "@jugl-web/ui-components/cross-platform";
import React, { useRef, useState } from "react";
import { cx, isFileTypeAccepted, useTranslations } from "@jugl-web/utils";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as PictureIcon } from "./assets/picture.svg";

export interface DroppableFileInputProps {
  onUpdate: (files?: File[]) => void;
  isProcessing?: boolean;
  accept?: string;
  validateType?: boolean;
  multiple?: boolean;
  customContent?: React.ReactNode;
  customIcon?: React.ReactNode;
  value?: {
    files?: File[];
    state?: { type: "success" | "error"; title: string; info?: string };
  };
  onIncorrectFile?: () => void;
  isInvalid?: boolean;
  isDisabled?: boolean;
}

export const DroppableFileInput: React.FC<DroppableFileInputProps> = ({
  onUpdate,
  onIncorrectFile,
  value,
  multiple,
  accept = "*",
  isProcessing,
  validateType,
  customContent,
  isInvalid,
  isDisabled,
  customIcon,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslations();

  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const title = value?.state?.title;
  const info = value?.state?.info;
  const type = value?.state?.type;

  const processFiles = (files: File[]) => {
    if (!validateType) {
      onUpdate(multiple ? files : files.slice(0, 1));
      return;
    }

    const acceptedFiles = files.filter((file) =>
      isFileTypeAccepted(file, accept)
    );

    if (files.length !== acceptedFiles.length && onIncorrectFile) {
      onIncorrectFile();
    }

    onUpdate(multiple ? acceptedFiles : acceptedFiles.slice(0, 1));
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingOver(false);
    if (!multiple && value?.files?.length) return;

    const droppedFiles = Array.from(event.dataTransfer.files);
    processFiles(droppedFiles);
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = Array.from(event.target.files || []);
    processFiles(selectedFiles);
  };

  return (
    <div
      onDrop={(e) => !isDisabled && handleDrop(e)}
      onDragOver={(e) => !isDisabled && e.preventDefault()}
      onDragEnter={() => !isDisabled && setIsDraggingOver(true)}
      onDragLeave={() => !isDisabled && setIsDraggingOver(false)}
      onClick={() => {
        if (isDisabled) return;
        if (!value?.files?.length || multiple) {
          inputRef.current?.click();
        }
      }}
      className={cx(
        "flex h-[200px] w-full items-center justify-center truncate rounded-2xl border border-dashed bg-grey-100 p-6 transition-colors hover:bg-grey-200",
        (!value?.files?.length || multiple) && !isDisabled && "cursor-pointer",
        isDraggingOver && "bg-grey-200",
        type === "success" && "border-none bg-[#E8F7FC] hover:bg-[#E8F7FC]",
        type === "error" && "border-none bg-tertiary-50 hover:bg-tertiary-50",
        isInvalid
          ? "border-tertiary-400"
          : "border-messages-chat-bubble-sender",
        isDisabled && "pointer-events-none opacity-40"
      )}
    >
      {(() => {
        if (isProcessing) {
          return <LoadingAnimation size="xl" />;
        }

        if (value?.state) {
          return (
            <div className="w-full text-center">
              {title && (
                <div className="truncate font-secondary leading-[140%] text-dark">
                  {title}
                </div>
              )}
              {info && (
                <div className="mt-2 truncate font-secondary text-sm leading-[140%] text-dark-700">
                  {info}
                </div>
              )}
              {!!value.files?.length && !multiple && (
                <div className="mx-auto mt-4 flex w-min max-w-[200px] items-center justify-center gap-2 truncate rounded-lg border border-solid border-grey-300 bg-white py-1.5 pr-2.5 pl-4">
                  <span className="truncate leading-[140%] text-dark">
                    {value.files?.[0].name}
                  </span>
                  <PlainButton onClick={() => onUpdate()}>
                    <CloseIcon />
                  </PlainButton>
                </div>
              )}
            </div>
          );
        }

        return (
          <div className="flex flex-col items-center justify-center gap-3">
            {customIcon || <PictureIcon />}
            {customContent || (
              <span className="font-dark font-secondary text-lg leading-[27px] tracking-[0.18px]">
                {t({
                  id: "droppable-file-input-component.drop-files-here",
                  defaultMessage: "Drop files here",
                })}
              </span>
            )}
          </div>
        );
      })()}
      <input
        type="file"
        ref={inputRef}
        hidden
        disabled={isDisabled}
        multiple={multiple}
        accept={accept}
        onChange={(e) => {
          handleFileInputChange(e);
          e.target.value = "";
        }}
      />
    </div>
  );
};
